/**
 * Logo-branding add-on.
 *
 * Shopify line item properties cannot change price, so the "+Rs. 60/box"
 * is charged by adding a second cart line for a hidden add-on product,
 * kept at the same quantity as the hamper it belongs to.
 *
 * The two lines are linked by the `_pv_addon_for` property. Properties
 * beginning with an underscore are hidden from the customer in cart and
 * checkout, but still reach the order, so staff can see the pairing.
 *
 * Responsibilities:
 *   1. On the product page, add the add-on line after the hamper is added.
 *   2. Enforce the minimum quantity before allowing add to cart.
 *   3. In the cart, keep add-on quantities matched to their parent, and
 *      drop orphaned add-on lines when the parent is removed.
 */
(function () {
  'use strict';

  var LINK_PROP = '_pv_addon_for';

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) {
      return r.json();
    });
  }

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
      return r.json();
    });
  }

  /* ---------------- Product page ---------------- */

  function initProductForm(root) {
    var addonVariant = root.getAttribute('data-addon-variant');
    var minQty = parseInt(root.getAttribute('data-addon-min'), 10) || 0;
    var toggle = root.querySelector('[data-pv-logo-toggle]');
    var errorEl = root.querySelector('[data-pv-logo-error]');
    if (!toggle) return;

    var formId = toggle.getAttribute('form');
    var form = formId ? document.getElementById(formId) : toggle.closest('form');
    if (!form) return;

    function quantityInput() {
      // Dawn renders the quantity selector as a sibling of the form, associated
      // by the `form` attribute rather than nested inside it, so a querySelector
      // scoped to the form misses it. form.elements includes both.
      return form.elements ? form.elements.namedItem('quantity') : null;
    }

    function currentQty() {
      var q = quantityInput();
      return q ? parseInt(q.value, 10) || 1 : 1;
    }

    function validate() {
      if (!toggle.checked || minQty <= 0) {
        if (errorEl) errorEl.hidden = true;
        return true;
      }
      var ok = currentQty() >= minQty;
      if (errorEl) errorEl.hidden = ok;
      return ok;
    }

    toggle.addEventListener('change', validate);

    var qtyEl = quantityInput();
    if (qtyEl) {
      ['input', 'change'].forEach(function (evt) {
        qtyEl.addEventListener(evt, validate);
      });
      // Dawn's +/- buttons set .value directly, which fires no event in some
      // paths, so re-check on click of the surrounding quantity widget too.
      var wrap = qtyEl.closest('.quantity');
      if (wrap) wrap.addEventListener('click', function () { window.setTimeout(validate, 50); });
    }

    form.addEventListener(
      'submit',
      function (e) {
        if (!toggle.checked) return;

        if (!validate()) {
          e.preventDefault();
          e.stopImmediatePropagation();
          return;
        }

        // No add-on product configured: leave the property as a flag only.
        if (!addonVariant) return;

        // Let the theme's own add-to-cart run, then attach the add-on line.
        var qty = currentQty();
        var key = 'pv-' + Date.now();
        var linkField = form.querySelector('[name="properties[' + LINK_PROP + ']"]');
        if (!linkField) {
          linkField = document.createElement('input');
          linkField.type = 'hidden';
          linkField.name = 'properties[' + LINK_PROP + ']';
          form.appendChild(linkField);
        }
        linkField.value = key;

        window.setTimeout(function () {
          postJSON('/cart/add.js', {
            items: [
              {
                id: parseInt(addonVariant, 10),
                quantity: qty,
                properties: (function () {
                  var p = {};
                  p[LINK_PROP] = key;
                  return p;
                })(),
              },
            ],
          })
            .then(function () {
              document.dispatchEvent(new CustomEvent('pv:addon:added'));
            })
            .catch(function () {
              /* Charging the add-on failed; the property still records the request. */
            });
        }, 350);
      },
      true
    );
  }

  /* ---------------- Cart reconciliation ---------------- */

  function linkKey(item) {
    return (item.properties && item.properties[LINK_PROP]) || null;
  }

  function reconcileCart() {
    return getCart().then(function (cart) {
      var parents = {};
      var addons = [];

      cart.items.forEach(function (item) {
        var key = linkKey(item);
        if (!key) return;
        // The hamper carries visible properties too; the bare add-on does not.
        if (Object.keys(item.properties).length > 1) parents[key] = item;
        else addons.push(item);
      });

      var updates = {};
      var changed = false;

      addons.forEach(function (addon) {
        var key = linkKey(addon);
        var parent = parents[key];
        if (!parent) {
          // Parent gone: drop the orphaned add-on.
          updates[addon.key] = 0;
          changed = true;
        } else if (parent.quantity !== addon.quantity) {
          updates[addon.key] = parent.quantity;
          changed = true;
        }
      });

      if (!changed) return null;
      return postJSON('/cart/update.js', { updates: updates });
    });
  }

  /* Add-on lines come back from Shopify ahead of their parent, which reads oddly:
     the fee appears before the thing it belongs to. Move them to the end of the
     list. Dawn replaces the cart markup on every quantity change, so re-apply
     whenever the table's contents change. */
  function moveAddonsLast() {
    document.querySelectorAll('.cart-item--pv-addon').forEach(function (row) {
      var body = row.parentNode;
      if (body && row !== body.lastElementChild) body.appendChild(row);
    });
  }

  function watchCartOrder() {
    moveAddonsLast();
    var body = document.querySelector('.cart-item') && document.querySelector('.cart-item').parentNode;
    if (!body) return;
    var pending = false;
    new MutationObserver(function () {
      if (pending) return;
      pending = true;
      window.setTimeout(function () {
        pending = false;
        moveAddonsLast();
      }, 60);
    }).observe(body, { childList: true });
  }

  function initCart() {
    if (!/\/cart/.test(window.location.pathname)) return;
    watchCartOrder();
    reconcileCart().then(function (res) {
      if (res) window.location.reload();
    });
  }

  function init() {
    document.querySelectorAll('[data-pv-personalisation]').forEach(initProductForm);
    initCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
